import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import {
  Buildings, ShieldCheck, SignOut, ArrowClockwise, Ticket, Users, Bell, Eye,
  CurrencyCircleDollar, Plus, Trash, ArrowLeft, Warning,
} from "@phosphor-icons/react";
import api, { fmtGNF, fmtDate, apiError } from "../lib/api";

const TIER_KEYS = ["tier1", "tier2", "tier3", "tier4"];
const TIER_LABELS = { tier1: "Starter", tier2: "Pro", tier3: "Business", tier4: "Sur mesure" };
const PAY_STATUS = [["trial", "Essai"], ["paid", "À jour"], ["late", "En retard"]];
const PAY_CLASS = { trial: "badge-partial", paid: "badge-paid", late: "badge-unpaid" };
const TABS = [["overview", "Vue d'ensemble"], ["companies", "Entreprises"], ["tickets", "Tickets"],
              ["agents", "Agents support"], ["leads", "Demandes"], ["audit", "Journal"]];

function Login({ onOk }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const step1 = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/login", form);
      setChallenge(data);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const step2 = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/admin/login/2fa", { challenge_id: challenge.challenge_id, code });
      onOk();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#16233D]">
      <form onSubmit={challenge ? step2 : step1} className="w-full max-w-sm bg-white rounded-[24px] p-6 space-y-4 fade-in" data-testid="admin-login-form">
        <p className="font-display font-bold text-2xl lowercase tracking-tight">kavizio <span className="text-[#C9793A]">admin</span></p>
        <p className="text-xs text-gray-500">Panneau interne — équipe Kavizio uniquement. Aucun lien avec les comptes clients.</p>
        {error && <p data-testid="admin-login-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}
        {challenge ? (
          <>
            <p data-testid="admin-2fa-code" className="text-sm bg-[#F7EFD9] text-[#9a7620] rounded-xl px-3 py-2">
              Code envoyé au {challenge.phone_masked} (SMS simulé) : <span data-testid="admin-2fa-code-value" className="font-mono-doc font-bold">{challenge.code}</span>
            </p>
            <input data-testid="admin-2fa-input" className="kinput font-mono-doc" placeholder="Code de vérification" value={code} onChange={(e) => setCode(e.target.value)} required />
            <button data-testid="admin-2fa-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Vérification..." : "Valider le code"}</button>
          </>
        ) : (
          <>
            <input data-testid="admin-email-input" className="kinput" type="email" placeholder="Email administrateur" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input data-testid="admin-password-input" className="kinput" type="password" placeholder="Mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <button data-testid="admin-login-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Connexion..." : "Continuer"}</button>
          </>
        )}
      </form>
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return <p className="text-sm text-gray-400">Chargement...</p>;
  const cards = [["Entreprises inscrites", stats.companies], ["Revenu mensuel récurrent", fmtGNF(stats.mrr)],
                 ["Tickets ouverts", stats.open_tickets], ["Paiements en retard", stats.late_payments],
                 ["Nouvelles demandes", stats.new_leads]];
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map(([l, v]) => (
          <div key={l} className="kcard p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{l}</p>
            <p className="font-display font-bold text-xl tabular-nums">{v}</p>
          </div>
        ))}
      </div>
      <div className="kcard p-5">
        <h3 className="font-display font-bold mb-3">Répartition par palier</h3>
        {stats.by_tier.map((t) => (
          <div key={t.tier} className="flex items-center gap-3 text-sm py-1.5">
            <span className="flex-1">{t.label}</span>
            <span className="font-semibold tabular-nums">{t.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingBox({ company, onDone }) {
  const [form, setForm] = useState({ mode: "percent", value: "", end_date: "", note: "" });
  const save = async () => {
    try {
      await api.post(`/admin/companies/${company.id}/pricing`, {
        mode: form.mode, value: parseFloat(form.value) || 0, end_date: form.end_date || null, note: form.note });
      toast.success("Tarification ajustée — action tracée"); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  const clear = async () => {
    try { await api.delete(`/admin/companies/${company.id}/pricing`); toast.success("Retour au tarif standard"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="kcard p-4 space-y-3" data-testid="pricing-box">
      <h4 className="font-display font-bold text-sm">Tarification individuelle</h4>
      <p className="text-xs text-gray-500">
        Tarif standard : {company.pricing.base ? fmtGNF(company.pricing.base) : "sur devis"} ·
        Payé par le client : <span className="font-semibold">{fmtGNF(company.pricing.final)}</span>
        {company.pricing.custom && <> (ajusté{company.pricing.end_date ? ` jusqu'au ${company.pricing.end_date}` : ""})</>}
      </p>
      {company.pricing_note && <p className="text-xs text-[#9a7620]">Note interne : {company.pricing_note}</p>}
      <div className="grid sm:grid-cols-3 gap-2">
        <select data-testid="pricing-mode-select" className="kinput" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
          <option value="percent">Réduction en %</option>
          <option value="amount">Réduction en GNF</option>
          <option value="fixed">Prix fixe négocié (GNF)</option>
        </select>
        <input data-testid="pricing-value-input" type="number" className="kinput" placeholder="Valeur" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
        <input data-testid="pricing-end-input" type="date" className="kinput" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
      </div>
      <input data-testid="pricing-note-input" className="kinput" placeholder="Note interne obligatoire (raison du geste commercial)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      <div className="flex gap-2">
        <button data-testid="pricing-save-btn" className="btn-amber py-2 px-4 text-sm" disabled={!form.value || !form.note.trim()} onClick={save}>Appliquer</button>
        {company.pricing.custom && <button data-testid="pricing-clear-btn" className="btn-ghost py-2 px-4 text-sm" onClick={clear}>Revenir au tarif standard</button>}
      </div>
      <p className="text-xs text-gray-400">Le client ne voit que le prix final. La note interne ne lui est jamais affichée.</p>
    </div>
  );
}

function CompanyHeader({ c }) {
  return (
    <div className="kcard p-5 space-y-2">
      <h2 className="font-display font-bold text-xl flex items-center gap-2">
        <Buildings size={20} /> {c.name}
        {c.suspended && <span className="badge-unpaid px-2 py-0.5 rounded-full text-xs font-semibold">Suspendu</span>}
      </h2>
      <p className="text-sm text-gray-600">{c.sector || "Secteur non précisé"} · inscrit le {fmtDate(c.created_at)} · {c.active_users} utilisateur(s) actif(s)</p>
      {c.patron && <p className="text-sm text-gray-600">Patron : {c.patron.name} · {c.patron.email} · {c.patron.phone}</p>}
    </div>
  );
}

function PillRow({ label, options, current, onPick, testPrefix }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([k, l]) => (
          <button key={k} data-testid={`${testPrefix}-${k}`} onClick={() => onPick(k)}
            className={`text-xs px-2.5 py-1.5 rounded-full border ${current === k ? "bg-[#16233D] text-white border-[#16233D] font-semibold" : "bg-white border-[#E0DACE]"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function DangerZone({ c, id, patch, onBack, onRefresh, reload }) {
  const [del, setDel] = useState("");
  const [reason, setReason] = useState("");

  const reinvite = async () => {
    try {
      const { data } = await api.post(`/admin/companies/${id}/reinvite-patron`);
      navigator.clipboard.writeText(`${window.location.origin}${data.invite_path}`).catch(() => {});
      toast.success("Nouveau lien d'invitation du patron copié");
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancel = async () => {
    if (!window.confirm("Enregistrer la résiliation de ce compte ?")) return;
    await api.post(`/admin/companies/${id}/cancel`).catch((e) => toast.error(apiError(e)));
    reload(); onRefresh();
  };

  const destroy = async () => {
    try {
      await api.delete(`/admin/companies/${id}`, { data: { confirm_name: del, reason } });
      toast.success("Compte supprimé définitivement"); onBack(); onRefresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="kcard p-4 flex flex-wrap gap-2 items-center">
      {c.suspended
        ? <button data-testid="admin-reactivate-btn" onClick={() => patch({ suspended: false })} className="btn-ghost py-2 px-3 text-xs font-semibold text-[#2E7D5B]">Réactiver le compte</button>
        : <button data-testid="admin-suspend-btn" onClick={() => patch({ suspended: true })} className="btn-ghost py-2 px-3 text-xs font-semibold text-[#B4432F]">Suspendre le compte</button>}
      <button data-testid="company-reinvite-patron-btn" onClick={reinvite} className="btn-ghost py-2 px-3 text-xs font-semibold">Régénérer l'invitation du patron</button>
      {!c.cancelled_at && (
        <button data-testid="admin-cancel-btn" onClick={cancel} className="btn-ghost py-2 px-3 text-xs font-semibold">Enregistrer une résiliation</button>
      )}
      {c.cancelled_at && (
        <div className="w-full space-y-2">
          <p className="text-xs text-[#B4432F] flex items-center gap-1.5"><Warning size={14} /> Résilié le {fmtDate(c.cancelled_at)} — suppression définitive possible 12 mois après.</p>
          <div className="flex gap-2 flex-wrap">
            <input data-testid="admin-delete-reason-input" className="kinput flex-1 min-w-[200px]" placeholder="Raison du départ (conservée dans l'historique)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <input data-testid="admin-delete-confirm-input" className="kinput flex-1 min-w-[200px]" placeholder={`Tapez « ${c.name} » pour confirmer`} value={del} onChange={(e) => setDel(e.target.value)} />
            <button data-testid="admin-delete-btn" className="btn-ghost py-2 px-3 text-xs font-semibold text-[#B4432F] flex items-center gap-1" disabled={del !== c.name} onClick={destroy}>
              <Trash size={13} /> Supprimer définitivement
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Tier4Box({ c, patch }) {
  const [price, setPrice] = useState(String(c.pricing?.final || ""));
  const [maxEst, setMaxEst] = useState(String(c.max_establishments || ""));
  const [note, setNote] = useState("");
  const isTier4 = c.tier === "tier4";
  return (
    <div className="kcard p-4 space-y-2" data-testid="tier4-box">
      <h4 className="font-display font-bold text-sm">Palier 4 — conditions négociées</h4>
      <p className="text-xs text-gray-500">
        {isTier4
          ? "Prix mensuel négocié obligatoire. Le nombre maximum d'établissements correspond à ce qui a été facturé."
          : "Saisissez le prix négocié puis activez le Palier 4 : aucun compte Palier 4 ne peut exister sans prix défini."}
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        <input data-testid="tier4-price-input" type="number" className="kinput" placeholder="Prix mensuel négocié (GNF)"
          value={price} onChange={(e) => setPrice(e.target.value)} />
        <input data-testid="tier4-maxest-input" type="number" className="kinput" placeholder="Nombre max. d'établissements"
          value={maxEst} onChange={(e) => setMaxEst(e.target.value)} />
      </div>
      <input data-testid="tier4-note-input" className="kinput" placeholder="Note interne obligatoire (conditions négociées)"
        value={note} onChange={(e) => setNote(e.target.value)} />
      <button data-testid="tier4-apply-btn" className="btn-amber py-2 px-4 text-sm"
        disabled={!price || !note.trim()}
        onClick={() => patch({ tier: "tier4", custom_price: parseFloat(price), note,
                               ...(maxEst ? { max_establishments: parseInt(maxEst, 10) } : {}) })}>
        {isTier4 ? "Mettre à jour les conditions" : "Activer le Palier 4 avec ce prix"}
      </button>
    </div>
  );
}

function CompanyTickets({ c, onNewTicket, onMirror }) {  const openTickets = c.tickets.filter((t) => t.status !== "Résolu");
  return (
    <div className="kcard p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-bold text-sm">Tickets de support</h4>
        <button data-testid="company-new-ticket-btn" onClick={onNewTicket} className="btn-ghost py-1.5 px-3 text-xs flex items-center gap-1"><Plus size={12} /> Nouveau ticket</button>
      </div>
      {c.tickets.length === 0 && <p className="text-sm text-gray-400">Aucun ticket.</p>}
      <div className="space-y-1.5">
        {c.tickets.map((t) => (
          <div key={t.id} data-testid="company-ticket-row" className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-sm flex-wrap">
            <span className="font-mono-doc text-xs">#{t.number}</span>
            <span className="flex-1 min-w-0 truncate">{t.subject}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.status === "Résolu" ? "badge-paid" : "badge-partial"}`}>{t.status}</span>
            {t.status !== "Résolu" && (
              <button data-testid="mirror-start-btn" onClick={() => onMirror(t.id)} className="text-xs text-[#C9793A] font-semibold flex items-center gap-1"><Eye size={13} /> Voir en tant que</button>
            )}
          </div>
        ))}
      </div>
      {openTickets.length === 0 && <p className="text-xs text-gray-400 mt-2">La vue miroir exige un ticket ouvert.</p>}
    </div>
  );
}

function CompanyPayments({ payments }) {
  return (
    <div className="kcard p-4">
      <h4 className="font-display font-bold text-sm mb-2 flex items-center gap-1.5"><CurrencyCircleDollar size={16} /> Paiements d'abonnement</h4>
      {payments.length === 0 && <p className="text-sm text-gray-400">Aucun paiement enregistré. L'encaissement en ligne arrive à la prochaine étape.</p>}
      {payments.map((p) => (
        <div key={p.id} className="flex items-center gap-3 text-sm px-3 py-2 rounded-xl bg-[#F6F4EF]/60">
          <span className="flex-1">{fmtDate(p.created_at)}</span>
          <span className="font-semibold tabular-nums">{fmtGNF(p.amount)}</span>
          <span className="text-xs">{p.status}</span>
        </div>
      ))}
    </div>
  );
}

function CompanyDetail({ id, me, onBack, onRefresh }) {
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/admin/companies/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    try { await api.patch(`/admin/companies/${id}`, updates); toast.success("Mis à jour — tracé"); load(); onRefresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const startMirror = async (ticketId) => {
    try {
      const { data } = await api.post("/admin/mirror/start", { company_id: id, ticket_id: ticketId });
      toast.success(`Vue miroir ouverte (${data.level === "full" ? "contrôle complet" : "lecture seule"})`);
      window.location.href = "/accueil";
    } catch (e) { toast.error(apiError(e)); }
  };

  const newTicket = async () => {
    const subject = window.prompt("Objet du ticket pour cette entreprise ?");
    if (!subject) return;
    try { await api.post("/admin/tickets", { company_id: id, subject }); toast.success("Ticket créé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!c) return <p className="text-sm text-gray-400">Chargement...</p>;

  return (
    <div className="space-y-4" data-testid="admin-company-detail">
      <button data-testid="company-back-btn" onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500"><ArrowLeft size={15} /> Toutes les entreprises</button>
      <CompanyHeader c={c} />
      {me.role === "admin" && (
        <>
          <div className="kcard p-4 grid sm:grid-cols-2 gap-4">
            <PillRow label="Palier d'abonnement" testPrefix="admin-tier" current={c.tier}
              options={TIER_KEYS.map((k) => [k, TIER_LABELS[k]])}
              onPick={(k) => (k === "tier4"
                ? toast.info("Palier 4 négocié : saisissez le prix mensuel dans le bloc « conditions négociées » ci-dessous.")
                : patch({ tier: k }))} />
            <PillRow label="Statut de paiement" testPrefix="admin-pay" current={c.payment_status}
              options={PAY_STATUS} onPick={(k) => patch({ payment_status: k })} />
          </div>
          <Tier4Box c={c} patch={patch} />
          <PricingBox company={c} onDone={() => { load(); onRefresh(); }} />
          <DangerZone c={c} id={id} patch={patch} onBack={onBack} onRefresh={onRefresh} reload={load} />
        </>
      )}
      <CompanyTickets c={c} onNewTicket={newTicket} onMirror={startMirror} />
      <CompanyPayments payments={c.payments} />
    </div>
  );
}

function Companies({ me, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ search: "", tier: "", payment_status: "" });
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_name: "", sector: "", email: "", phone: "", tier: "tier1",
                                     custom_price: "", max_establishments: "", note: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api.get(`/admin/companies?${p.toString()}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/companies", {
        ...form,
        custom_price: form.custom_price ? parseFloat(form.custom_price) : null,
        max_establishments: form.max_establishments ? parseInt(form.max_establishments, 10) : null,
      });
      navigator.clipboard.writeText(`${window.location.origin}${data.invite_path}`).catch(() => {});
      toast.success("Entreprise créée — lien d'invitation du patron copié");
      setShowCreate(false);
      setForm({ company_name: "", sector: "", email: "", phone: "", tier: "tier1",
                custom_price: "", max_establishments: "", note: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (selected) return <CompanyDetail id={selected} me={me} onBack={() => setSelected(null)} onRefresh={() => { load(); onRefresh(); }} />;

  return (
    <div className="space-y-3" data-testid="admin-companies">
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="admin-search-input" className="kinput flex-1 min-w-[180px]" placeholder="Rechercher une entreprise" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
        <select data-testid="admin-filter-tier" className="kinput max-w-[160px]" value={filters.tier} onChange={(e) => setFilters({ ...filters, tier: e.target.value })}>
          <option value="">Tous les paliers</option>
          {TIER_KEYS.map((k) => <option key={k} value={k}>{TIER_LABELS[k]}</option>)}
        </select>
        <select data-testid="admin-filter-payment" className="kinput max-w-[160px]" value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}>
          <option value="">Tous les statuts</option>
          {PAY_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button data-testid="admin-create-company-btn" onClick={() => setShowCreate(true)} className="btn-amber py-2 px-4 text-sm flex items-center gap-1.5"><Plus size={15} /> Créer une entreprise</button>
      </div>

      {rows.map((c) => (
        <button key={c.id} data-testid="admin-company-row" onClick={() => setSelected(c.id)}
          className="w-full text-left kcard p-4 flex items-center gap-3 flex-wrap hover:shadow-md transition-shadow">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm flex items-center gap-2">
              {c.name}
              {c.suspended && <span className="badge-unpaid px-2 py-0.5 rounded-full text-xs font-semibold">Suspendu</span>}
            </p>
            <p className="text-xs text-gray-500">{TIER_LABELS[c.tier]} · inscrit le {fmtDate(c.created_at)} · {c.active_users} utilisateur(s)</p>
          </div>
          <span className="text-sm font-semibold tabular-nums">{fmtGNF(c.pricing.final)}{c.pricing.custom && <span className="text-xs text-[#C9793A]"> ajusté</span>}</span>
          <span className={`${PAY_CLASS[c.payment_status]} px-2.5 py-1 rounded-full text-xs font-semibold`}>
            {(PAY_STATUS.find(([k]) => k === c.payment_status) || ["", c.payment_status])[1]}
          </span>
        </button>
      ))}
      {rows.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucune entreprise ne correspond.</div>}

      {showCreate && (
        <ModalShell size="max-w-md" onClose={() => setShowCreate(false)}>
            <h3 className="font-display font-bold text-lg">Créer une entreprise cliente</h3>
            <p className="text-xs text-gray-500">Le patron reçoit une invitation et définit lui-même son mot de passe — vous ne le connaîtrez jamais.</p>
            <input data-testid="create-company-name" className="kinput" placeholder="Nom de l'entreprise" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            <input data-testid="create-company-sector" className="kinput" placeholder="Secteur" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
            <input data-testid="create-company-email" className="kinput" type="email" placeholder="Email du patron" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="create-company-phone" className="kinput" placeholder="Téléphone du patron" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <select data-testid="create-company-tier" className="kinput" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              {TIER_KEYS.map((k) => <option key={k} value={k}>{TIER_LABELS[k]}</option>)}
            </select>
            {form.tier === "tier4" && (
              <div className="space-y-2 bg-[#F6F4EF] rounded-2xl p-3" data-testid="create-company-tier4">
                <p className="text-xs text-gray-600">Palier 4 négocié : le prix mensuel est obligatoire.</p>
                <input data-testid="create-company-price" type="number" className="kinput" placeholder="Prix mensuel négocié (GNF)"
                  value={form.custom_price} onChange={(e) => setForm({ ...form, custom_price: e.target.value })} />
                <input data-testid="create-company-maxest" type="number" className="kinput" placeholder="Nombre max. d'établissements"
                  value={form.max_establishments} onChange={(e) => setForm({ ...form, max_establishments: e.target.value })} />
                <input data-testid="create-company-note" className="kinput" placeholder="Note interne (conditions négociées)"
                  value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            )}
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowCreate(false)}>Annuler</button>
              <button data-testid="create-company-submit" className="btn-amber flex-1 py-2.5"
                disabled={!form.company_name || !form.email || !form.phone || (form.tier === "tier4" && !form.custom_price)}
                onClick={create}>Créer</button>
            </div>
          </ModalShell>
      )}
    </div>
  );
}

function Tickets() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => api.get(`/admin/tickets${status ? `?status=${encodeURIComponent(status)}` : ""}`)
    .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), [status]);
  useEffect(() => { load(); }, [load]);

  const setTicketStatus = async (t, s) => {
    try { await api.patch(`/admin/tickets/${t.id}`, { status: s }); toast.success("Ticket mis à jour"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="admin-tickets">
      <div className="flex gap-1.5">
        {[["", "Tous"], ["Ouvert", "Ouverts"], ["En cours", "En cours"], ["Résolu", "Résolus"]].map(([v, l]) => (
          <button key={v} data-testid={`ticket-filter-${v || "all"}`} onClick={() => setStatus(v)}
            className={`text-xs px-3 py-1.5 rounded-full border ${status === v ? "bg-[#16233D] text-white border-[#16233D] font-semibold" : "bg-white border-[#E0DACE]"}`}>{l}</button>
        ))}
      </div>
      {rows.map((t) => (
        <div key={t.id} data-testid="admin-ticket-row" className="kcard p-4 flex items-center gap-3 flex-wrap">
          <span className="font-mono-doc text-xs">#{t.number}</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{t.subject}</p>
            <p className="text-xs text-gray-500">{t.company_name} · {t.assignee_name} · {fmtDate(t.created_at)}</p>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.status === "Résolu" ? "badge-paid" : "badge-partial"}`}>{t.status}</span>
          {["Ouvert", "En cours", "Résolu"].filter((s) => s !== t.status).map((s) => (
            <button key={s} data-testid={`ticket-set-${s}`} onClick={() => setTicketStatus(t, s)} className="text-xs text-[#C9793A] font-semibold">{s}</button>
          ))}
        </div>
      ))}
      {rows.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucun ticket.</div>}
    </div>
  );
}

function Agents() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", mirror_level: "read" });
  const load = useCallback(() => api.get("/admin/agents").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    try {
      const { data } = await api.post("/admin/agents", form);
      navigator.clipboard.writeText(`${window.location.origin}${data.invite_path}`).catch(() => {});
      toast.success("Invitation créée — lien copié. L'agent définira lui-même son mot de passe.");
      setForm({ name: "", email: "", mirror_level: "read" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/admin/agents/${id}`, updates); toast.success("Agent mis à jour — tracé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="admin-agents">
      <div className="kcard p-4 space-y-2">
        <h3 className="font-display font-bold text-sm">Inviter un agent support</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <input data-testid="agent-name-input" className="kinput" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="agent-email-input" className="kinput" type="email" placeholder="Email interne" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select data-testid="agent-level-select" className="kinput" value={form.mirror_level} onChange={(e) => setForm({ ...form, mirror_level: e.target.value })}>
            <option value="read">Vue miroir : lecture seule</option>
            <option value="full">Vue miroir : contrôle complet</option>
          </select>
        </div>
        <button data-testid="agent-invite-btn" className="btn-amber py-2 px-4 text-sm" disabled={!form.name || !form.email} onClick={invite}>Créer l'invitation</button>
      </div>
      {rows.map((a) => (
        <div key={a.id} data-testid="admin-agent-row" className="kcard p-4 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{a.name} {a.role === "admin" && <span className="text-xs text-[#C9793A]">administrateur</span>}</p>
            <p className="text-xs text-gray-500">{a.email} · vue miroir : {a.mirror_level === "full" ? "contrôle complet" : "lecture seule"}</p>
          </div>
          {a.pending && <span className="badge-partial px-2.5 py-1 rounded-full text-xs font-semibold">Invitation en attente</span>}
          {a.role !== "admin" && (
            <>
              <button data-testid={`agent-toggle-level-${a.id}`} onClick={() => patch(a.id, { mirror_level: a.mirror_level === "full" ? "read" : "full" })}
                className="text-xs text-[#C9793A] font-semibold">{a.mirror_level === "full" ? "Retirer le contrôle complet" : "Accorder le contrôle complet"}</button>
              <button data-testid={`agent-toggle-active-${a.id}`} onClick={() => patch(a.id, { active: !a.active })}
                className={`text-xs font-semibold ${a.active ? "text-[#B4432F]" : "text-[#2E7D5B]"}`}>{a.active ? "Révoquer" : "Réactiver"}</button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function Leads() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/leads").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="admin-leads">
      {rows.map((l) => (
        <div key={l.id} data-testid="admin-lead-row" className="kcard p-4">
          <p className="font-semibold text-sm">{l.company_name} <span className="font-normal text-gray-500">· {l.contact_name}</span></p>
          <p className="text-xs text-gray-500">{l.email} · {l.phone} · offre {l.interest} · {fmtDate(l.created_at)}</p>
          {l.message && <p className="text-sm mt-1.5">{l.message}</p>}
        </div>
      ))}
      {rows.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucune demande de contact.</div>}
    </div>
  );
}

function CompanyArchives() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/company-archives").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="kcard p-4 space-y-1.5" data-testid="admin-company-archives">
      <h3 className="font-display font-bold text-sm mb-1">Historique des entreprises supprimées (conservé indéfiniment)</h3>
      {rows.map((a) => (
        <div key={a.id} data-testid="company-archive-row" className="px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-xs">
          <p className="font-semibold">{a.name} <span className="font-normal text-gray-500">· palier {a.tier || "—"}</span></p>
          <p className="text-gray-500">Activité : {a.started_at ? fmtDate(a.started_at) : "—"} → {a.ended_at ? fmtDate(a.ended_at) : "—"}</p>
          {a.departure_reason && <p className="text-[#C99A2E]">Raison du départ : {a.departure_reason}</p>}
        </div>
      ))}
      {rows.length === 0 && <p className="text-sm text-gray-400">Aucune entreprise supprimée définitivement.</p>}
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  const [actor, setActor] = useState("");
  const load = useCallback(() => api.get(`/admin/audit${actor ? `?actor=${encodeURIComponent(actor)}` : ""}`)
    .then((r) => setRows(r.data)).catch(() => {}), [actor]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-3" data-testid="admin-audit">
      <input data-testid="audit-actor-filter" className="kinput max-w-sm" placeholder="Filtrer par agent" value={actor} onChange={(e) => setActor(e.target.value)} />
      <div className="kcard p-4 space-y-1.5 max-h-[520px] overflow-y-auto">
        {rows.map((l) => (
          <div key={l.id} data-testid="admin-audit-row" className="px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-xs">
            <p className="font-semibold">{l.action.replaceAll("_", " ")} <span className="font-normal text-gray-500">· {l.actor} · {l.target_name} · {fmtDate(l.created_at)}{l.ticket_no ? ` · Ticket #${l.ticket_no}` : ""}</span></p>
            {l.old_value && <p className="text-gray-500">Avant : {JSON.stringify(l.old_value)}</p>}
            {l.new_value && <p className="text-gray-500">Après : {JSON.stringify(l.new_value)}</p>}
            {l.note && <p className="text-[#C99A2E]">Note : {l.note}</p>}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-gray-400">Aucune action enregistrée.</p>}
      </div>
      <CompanyArchives />
    </div>
  );
}

export function AdminAgentInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/admin/invite/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e))); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/invite/${token}/accept`, { password }); setDone(true); }
    catch (err) { setError(apiError(err)); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#16233D]">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-[24px] p-6 space-y-4" data-testid="agent-invite-form">
        <p className="font-display font-bold text-2xl lowercase tracking-tight">kavizio <span className="text-[#C9793A]">admin</span></p>
        {error && <p data-testid="agent-invite-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}
        {done ? (
          <>
            <p className="text-sm">Compte interne activé. Vous pouvez maintenant vous connecter au panneau.</p>
            <a href="/admin" className="btn-amber block text-center py-3">Aller au panneau</a>
          </>
        ) : info && (
          <>
            <p className="text-sm text-gray-600">Bonjour {info.name}, choisissez votre mot de passe interne (8 caractères minimum). Vous seul le connaîtrez.</p>
            <input data-testid="agent-invite-password" className="kinput" type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            <button data-testid="agent-invite-submit" className="btn-amber w-full py-3">Activer mon accès</button>
          </>
        )}
      </form>
    </div>
  );
}

export default function Admin() {
  const [me, setMe] = useState(null);
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const initialTabSet = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/me");
      setMe(r.data);
      setAuthed(true);
      if (!initialTabSet.current) {
        initialTabSet.current = true;
        setTab(r.data.role === "admin" ? "overview" : "tickets");
      }
      api.get("/admin/notifications").then((n) => setNotifs(n.data)).catch(() => {});
      if (r.data.role === "admin") api.get("/admin/overview").then((s) => setStats(s.data)).catch(() => {});
    } catch { setAuthed(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => { await api.post("/admin/logout").catch(() => {}); setAuthed(false); setMe(null); };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><p className="font-display font-bold text-xl lowercase animate-pulse">kavizio admin</p></div>;
  if (!authed) return <Login onOk={load} />;

  const tabs = TABS.filter(([k]) => me.role === "admin" || ["tickets"].includes(k));
  const unread = notifs.filter((n) => !n.read);

  return (
    <div className="min-h-screen bg-[#F6F4EF]">
      <header className="bg-[#16233D] text-white px-4 md:px-8 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-display font-bold text-2xl lowercase tracking-tight">kavizio <span className="text-[#C9793A]">admin</span></p>
            <p className="text-xs text-white/50">{me.name} · {me.role === "admin" ? "administrateur" : "agent support"} · vue miroir {me.mirror_level === "full" ? "contrôle complet" : "lecture seule"}</p>
          </div>
          <div className="flex gap-2 items-center">
            <span data-testid="admin-notif-count" className="text-xs px-3 py-2 rounded-xl bg-white/10 flex items-center gap-1.5"><Bell size={14} /> {unread.length}</span>
            <button data-testid="admin-refresh-btn" onClick={load} className="text-xs px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 flex items-center gap-1.5"><ArrowClockwise size={14} /> Rafraîchir</button>
            <button data-testid="admin-logout-btn" onClick={logout} className="text-xs px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 flex items-center gap-1.5"><SignOut size={14} /> Quitter</button>
          </div>
        </div>
        <nav className="flex gap-1.5 mt-4 flex-wrap">
          {tabs.map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`text-sm px-3.5 py-1.5 rounded-full transition-colors ${tab === k ? "bg-white text-[#16233D] font-semibold" : "bg-white/10 text-white/75 hover:bg-white/20"}`}>{l}</button>
          ))}
        </nav>
      </header>

      <main className="px-4 md:px-8 py-6 max-w-6xl space-y-5" data-testid="admin-panel">
        {unread.length > 0 && (
          <div className="kcard p-4 space-y-1.5" data-testid="admin-notifications">
            <h3 className="font-display font-bold text-sm flex items-center gap-1.5"><Bell size={15} /> Notifications</h3>
            {unread.map((n) => (
              <button key={n.id} data-testid="admin-notification" onClick={async () => { await api.post(`/admin/notifications/${n.id}/read`).catch(() => {}); load(); }}
                className="w-full text-left text-sm px-3 py-2 rounded-xl hover:bg-[#F6F4EF] flex gap-2">
                <span className={n.severity === "urgent" ? "text-[#B4432F]" : "text-[#3B6AA0]"}>•</span>
                <span className="flex-1">{n.message}</span>
                <span className="text-xs text-gray-400">{fmtDate(n.created_at)}</span>
              </button>
            ))}
          </div>
        )}
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "companies" && <Companies me={me} onRefresh={load} />}
        {tab === "tickets" && <Tickets />}
        {tab === "agents" && <Agents />}
        {tab === "leads" && <Leads />}
        {tab === "audit" && <AuditLog />}
        <p className="text-xs text-gray-400 flex items-center gap-1.5 pt-2">
          <ShieldCheck size={14} /> Toute action sur un compte client est tracée et visible par le client dans son propre journal.
          <Ticket size={14} className="ml-2" /> La vue miroir exige un ticket ouvert.
          <Users size={14} className="ml-2" /> Comptes internes sans aucun lien avec les comptes clients.
        </p>
      </main>
    </div>
  );
}
